import { ethers } from 'ethers';
import { ADDR, ABI, NET } from './config';

export const readProvider = new ethers.JsonRpcProvider(NET.rpc, NET.chainId, { staticNetwork: true });
export const contract = (name, runner) => new ethers.Contract(ADDR[name === 'AgentIDRegistry' ? 'AgentIDRegistry' : name], ABI[name], runner || readProvider);
export const fmt = (wei, d = 4) => Number(ethers.formatEther(wei ?? 0n)).toLocaleString(undefined, { maximumFractionDigits: d });
export const KIND = ['Miner', 'Pirate'];
export const ROUTE = ['Safe', 'Risky'];
export const TIER = ['Common', 'Rare', 'Epic', 'Legendary'];
// Unique hulls of the 20 Legendaries (#01-#10 miners, #11-#20 pirates); mirrors Ships.legendaryName on-chain.
export const LEGENDARY_NAMES = ['Sunforge', 'Monolith', 'Starbore', 'Halo Ark', 'Ore Cathedral', 'Twin Comet', 'Hex Hive',
  'Atlas Crane', 'Lotus Rig', 'Quarry Moon', 'Black Sun', 'Leviathan', 'Knife Crown', 'Abyss Wing', 'Bone Throne',
  'Chainsaw', 'Manta Doom', 'Gallowglass', 'Spider Queen', 'Red Comet'];
const nn = (n) => String(n).padStart(2, '0');
export const tierLabel = (sh) => (sh.serial ? `Legendary #${nn(sh.serial)} ${LEGENDARY_NAMES[sh.serial - 1]}` : sh.tier);
// Site preview art per kind/tier (+ the 20 Legendary GIFs), served from /nft/. The NFT itself (tokenURI) points at the gen0_v1
// per-variant art: <baseURI><kind>-<tier>/<variant>.png | <baseURI>legendary/legendary-NN.gif (IPFS upload pending Chen's go-ahead).
export const shipImg = (sh) => (sh.serial ? `/nft/legendary-${nn(sh.serial)}.gif`
  : `/nft/${String(sh.kind).toLowerCase()}-${String(sh.tier).toLowerCase()}.png`);

// 20% gas headroom on every wallet tx. The gas estimate is a dry run on the game's own RPC first, so a tx that would fail shows a
// plain error instead of a wallet popup, and the wallet does not need a working RPC of its own to estimate (a broken/slow
// MetaMask RPC for chain 97 made its estimate fail before any popup = "click does nothing"). Falls back to the wallet's estimate.
export const GAS_HEADROOM_NUM = 12n; export const GAS_HEADROOM_DEN = 10n;
export function withGasHeadroom(signer) {
  const orig = signer.sendTransaction.bind(signer);
  signer.sendTransaction = async (tx) => {
    if (tx.gasLimit == null) {
      let est;
      try { est = await readProvider.estimateGas({ ...tx, from: signer.address }); } catch (e) {
        if (isRevert(e)) throw e; // the contract would refuse it: show why, no popup
        est = await signer.estimateGas(tx);
      }
      tx = { ...tx, gasLimit: (est * GAS_HEADROOM_NUM) / GAS_HEADROOM_DEN };
    }
    return orig(tx);
  };
  return signer;
}
const isRevert = (e) => e?.code === 'CALL_EXCEPTION' || typeof (e?.data || e?.info?.error?.data || e?.error?.data) === 'string';

// eth_getLogs in chunks (public testnet RPCs cap the block range).
export async function getLogs(c, filter, fromBlock = ADDR.deployBlock) {
  const latest = await readProvider.getBlockNumber();
  if (!NET.logChunk) return c.queryFilter(filter, fromBlock, latest);
  const out = [];
  for (let b = fromBlock; b <= latest; b += NET.logChunk) {
    out.push(...(await c.queryFilter(filter, b, Math.min(latest, b + NET.logChunk - 1))));
  }
  return out;
}

// Plain-language text for contract errors users can hit.
const FRIENDLY = {
  MintClosed: 'Minting is not open right now.', BadCount: 'Choose between 1 and 10 ships.', SoldOut: 'All ships are sold out.',
  NoAgentID: 'This wallet does not hold an Agent ID.', HandleCapReached: 'This Agent ID has already minted its 10 ships.',
  StalePrice: 'The BNB price feed is out of date. Please try again in a few minutes.', Underpaid: 'Not enough BNB was sent. Refresh the price and try again.',
  NotReady: 'The random draw is not finished yet. Try again in a few seconds.', AlreadySettled: 'This has already been revealed.',
  NotOwner: 'You do not own this ship.', Locked: 'This ship is locked until its claim is finished.', TooEarly: 'Too early: the minimum staking time has not passed yet.',
  WrongKind: 'This action is not available for this ship type.', DailyCap: 'Daily limit reached. Try again tomorrow.', MaxLevel: 'This ship is already at max level.',
  NotOpen: 'This is not open yet.', NothingToRepair: 'This ship does not need repairs.', BadPrice: 'Enter a valid price.', NotListed: 'This ship is not for sale.',
  NotSeller: 'Only the seller can do this.', NotLaunched: 'The ORE pool has not launched yet.', LegendaryCapReached: 'All Legendary ships have been minted.',
  ERC20InsufficientBalance: 'Not enough ORE in your wallet.', ERC20InsufficientAllowance: 'ORE spending was not approved. Approve and try again.',
  ERC721InsufficientApproval: 'The game is not yet allowed to move this ship. Try again and confirm the "allow" step in your wallet.',
  ERC721IncorrectOwner: 'You do not own this ship.', ERC721NonexistentToken: 'This ship does not exist.',
  MaxOre: 'The ORE cost went up since your quote because the ORE price moved. Refresh and try again.', NoOracle: 'The ORE price is not available yet.',
  NoPool: 'The ORE pool has not opened yet.', PriceWarmup: 'The ORE price is warming up: it needs 30 minutes of pool history.',
  LowLiquidity: 'The ORE pool has too little liquidity to price ORE safely right now.',
  PriceOutOfBounds: 'The ORE price is outside the safe range right now. Please try again later.',
  OREBalance: 'Not enough ORE in your wallet.', OREAllowance: 'ORE spending was not approved. Approve and try again.',
  WrongChain: 'Your wallet is on another network. Switch it to the game network and try again.',
};
// Plain text for a reason string returned by view helpers (canMint / quoteFull / tryPrice).
export const reasonText = (r) => FRIENDLY[r] || String(r || '').replace(/([a-z])([A-Z])/g, '$1 $2');
// Human message from an ethers error, decoding custom errors with all game ABIs.
const ifaces = Object.values(ABI).map((a) => new ethers.Interface(a));
export function errMsg(e) {
  const data = e?.data || e?.info?.error?.data || e?.error?.data;
  if (typeof data === 'string' && data.length >= 10) {
    for (const i of ifaces) {
      try { const p = i.parseError(data); if (p) return FRIENDLY[p.name] || p.name.replace(/([a-z])([A-Z])/g, '$1 $2'); } catch (_) {}
    }
  }
  const raw = `${e?.shortMessage || ''} ${e?.message || ''} ${e?.info?.error?.message || ''}`;
  if (e?.code === 'ACTION_REJECTED' || e?.code === 4001 || e?.info?.error?.code === 4001 || /user (rejected|denied)/i.test(raw)) return 'You cancelled the request in your wallet.';
  if (e?.code === 'INSUFFICIENT_FUNDS' || /insufficient funds/i.test(raw)) return `Not enough ${NET.currency || 'BNB'} in your wallet to pay the network fee.`;
  if (e?.info?.error?.code === -32002 || /already pending/i.test(raw)) return 'Your wallet already has a request waiting. Open your wallet extension and answer it first.';
  if (/not been authorized|unauthorized|4100/i.test(raw)) return 'Your wallet has not given this site access. Reconnect your wallet and try again.';
  if (e?.code === 'CALL_EXCEPTION') return 'The game contract refused this action. Refresh the page and try again.';
  if (e?.code === 'NETWORK_ERROR' || e?.code === 'TIMEOUT' || e?.code === 'SERVER_ERROR' || /failed to fetch|rate limit|limit exceeded|timeout/i.test(raw)) return 'The network is busy or unreachable right now. Please try again in a moment.';
  const m = e?.shortMessage || e?.reason || e?.message || String(e);
  return m && m !== '[object Object]' && m !== 'undefined' ? m : 'Something went wrong. Please try again.';
}

// Ships the account holds directly, staked in the Fleet, or listed on the market.
export async function loadShips(account) {
  const ships = contract('Ships'); const fleet = contract('Fleet'); const market = contract('Marketplace');
  const me = account.toLowerCase();
  const ids = new Set();
  for (const ev of await getLogs(ships, ships.filters.Transfer(null, account))) ids.add(ev.args[2]);
  for (const ev of await getLogs(fleet, fleet.filters.Staked(account))) ids.add(ev.args[1]);
  for (const ev of await getLogs(market, market.filters.Listed(null, account))) ids.add(ev.args[0]);
  const rows = await Promise.all([...ids].map(async (id) => {
    const [owner, s] = await Promise.all([ships.ownerOf(id), ships.getShip(id)]);
    let where = null; let stake = null; let listing = null;
    if (owner.toLowerCase() === me) where = 'wallet';
    else if (owner === ADDR.Fleet) { stake = await fleet.stakes(id); if (stake.owner.toLowerCase() === me) where = 'staked'; }
    else if (owner === ADDR.Marketplace) { listing = await market.listings(id); if (listing.seller.toLowerCase() === me) where = 'listed'; }
    if (!where) return null;
    const [pending, power] = await Promise.all([where === 'staked' ? fleet.pendingReward(id) : 0n, ships.hashpowerOf(id).catch(() => 0n)]);
    return { id, kind: KIND[Number(s.kind)], gen: Number(s.gen), rank: Number(s.rank), tier: TIER[Number(s.tier)], serial: Number(s.serial), level: Number(s.level),
      durability: Number(s.durability), where, stake, listing, pending, power: Number(power) };
  }));
  return rows.filter(Boolean).sort((a, b) => Number(a.id - b.id));
}

// Chain time (not the browser clock): forks and testnets can drift.
export async function chainNow() {
  const b = await readProvider.getBlock('latest');
  return Number(b.timestamp);
}
